import * as React from 'react'
import { useLocation } from 'react-router-dom'

import { decodeQueryString, encodeQueryString } from '../../queryString'
import {
  DEFAULT_TYPES,
  FREE_TYPES,
  QuoteLongToShortType,
  QuoteNodeChartTypes,
  QuoteShortToLongType,
  QuoteUrlChartTypes,
  TypeBaseOption,
} from './utils'

export function useChartTypes({ isPremium }: { isPremium: boolean }) {
  const location = useLocation()
  const { b, t, ta, ty, p = 'd', r } = decodeQueryString(location.search)
  const taInt = ta ? Number.parseInt(ta) : 1
  const [selectedChartTypeOverwrite, setSelectedChartTypeOverwrite] = React.useState<string>()

  const generateRouterPath = React.useCallback(
    (type: QuoteNodeChartTypes) => {
      if (isPremium || FREE_TYPES.includes(type)) {
        return `/quote.ashx?${encodeQueryString({
          b,
          t,
          r,
          ty:
            type === QuoteNodeChartTypes.candlesticksimple
              ? QuoteLongToShortType.candlestick
              : QuoteLongToShortType[type],
          ta: type === QuoteNodeChartTypes.candlestick ? '1' : '0',
          p,
        })}`
      }
      return `/elite.ashx?${encodeQueryString({
        utm_source: 'finviz',
        utm_medium: 'banner',
        utm_campaign: 'quote-chart-type-select',
      })}`
    },
    [isPremium, b, t, r, p]
  )

  const chartTypes = React.useMemo(
    () =>
      (
        [
          !isPremium ? { icon: 'candleBarBasic', value: 'candlesticksimple', label: 'Candle - simple' } : null,
          ...DEFAULT_TYPES.map((type) =>
            !isPremium && type.value === 'candlestick' ? { ...type, label: 'Candle - advanced' } : type
          ),
        ].filter(Boolean) as TypeBaseOption[]
      ).map((item) => ({
        ...item,
        href: generateRouterPath(item.value as QuoteNodeChartTypes),
        reloadDocument: !isPremium,
      })),
    [generateRouterPath, isPremium]
  )

  const currentType = React.useMemo(
    () =>
      chartTypes.find((type) => {
        if (selectedChartTypeOverwrite) {
          return type.value === selectedChartTypeOverwrite
        }

        const defaultType =
          ((ty as QuoteUrlChartTypes | undefined) && QuoteShortToLongType[ty as QuoteUrlChartTypes]) ??
          (FinvizQuoteTypeCurrent as QuoteNodeChartTypes)

        return !isPremium && defaultType === QuoteNodeChartTypes.candlestick && taInt === 0
          ? type.value === QuoteNodeChartTypes.candlesticksimple
          : type.value === defaultType
      }),
    [chartTypes, selectedChartTypeOverwrite, ty, isPremium, taInt]
  )

  return { currentType, chartTypes, setSelectedChartTypeOverwrite }
}
